import React, { useState, useEffect, useRef, Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessage as ChatMessageType } from '../types/energyData';
import { format } from 'date-fns';
import { enUS, de } from 'date-fns/locale';
import { Bot, User, Thermometer, Zap, Sun, BarChart3 } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import MobileChart from './MobileChart';

// Error boundary for MobileChart fallback
class ErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('MobileChart error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const { t, i18n } = useTranslation();
  const isUser = message.type === 'user';
  const Icon = isUser ? User : Bot;
  
  // Detect mobile for conditional rendering
  // Check both window width and user agent for better mobile detection
  const detectMobile = () => {
    const width = window.innerWidth;
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    // Consider mobile if width < 640 OR if it's a mobile device (even if width is larger in dev tools)
    return width < 640 || isMobileDevice;
  };
  
  const [isMobile, setIsMobile] = useState(detectMobile());
  const chartInstanceRef = useRef<any>(null);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(detectMobile());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle legend click to toggle series visibility
  const handleLegendClick = (seriesName: string) => {
    const isHidden = hiddenSeries.has(seriesName);
    
    // For desktop (ECharts), use dispatchAction if chart instance exists
    if (!isMobile && chartInstanceRef.current) {
      chartInstanceRef.current.dispatchAction({
        type: isHidden ? 'legendSelect' : 'legendUnSelect',
        name: seriesName
      });
    }
    
    // Update local state (works for both mobile and desktop)
    setHiddenSeries(prev => {
      const newSet = new Set(prev);
      if (isHidden) {
        newSet.delete(seriesName);
      } else {
        newSet.add(seriesName);
      }
      return newSet;
    });
  };
  
  // Get the appropriate locale for date formatting
  const dateLocale = i18n.language === 'de' ? de : enUS;

  const getDataIcon = (type?: string) => {
    switch (type) {
      case 'pv_production':
        return <Sun className="w-4 h-4 text-yellow-500" />;
      case 'consumption':
      case 'consumption_7days':
        return <Zap className="w-4 h-4 text-blue-500" />;
      case 'room_temperature':
        return <Thermometer className="w-4 h-4 text-red-500" />;
      default:
        return <BarChart3 className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatValue = (value: number, unit: string) => {
    if (unit === 'kWh') {
      return `${value} kWh`;
    }
    return `${value} ${unit}`;
  };

  const buildChartOption = (chartData: NonNullable<ChatMessageType['chartData']>) => {
    // Helper function to extract unit from series (used in both yAxis and series)
    const getUnit = (s: any) => {
      if (s.unit) return s.unit;
      // Try to extract from name format "Label (TagId) (Unit)"
      const unitMatch = s.name.match(/\([^)]+\)\s*\(([^)]+)\)$/);
      if (unitMatch) return unitMatch[1];
      // Try format "Label (TagId)" - extract unit from tag ID
      const tagIdMatch = s.name.match(/\(([A-Z0-9]+)\)/);
      if (tagIdMatch) {
        const tagId = tagIdMatch[1];
        if (tagId.includes('Percent')) return '%';
        if (tagId.includes('Energy')) return 'kWh';
        if (tagId.includes('Power')) return 'W';
        if (tagId.includes('Temp')) return '°C';
        if (tagId.includes('Tariff')) return 'ct/kWh'; // CRITICAL: Add tariff unit detection
      }
      return '';
    };
    
    // Get distinct units to determine if we need multiple Y-axes
    const allUnits = chartData.series.map(getUnit).filter(u => u);
    const distinctUnits = Array.from(new Set(allUnits));
    const hasPercentages = distinctUnits.includes('%');
    const nonPercentUnits = distinctUnits.filter(u => u !== '%');
    const hasMultipleUnits = distinctUnits.length > 1;
    const option: any = {
      title: {
        // Hide title on mobile - we'll render it outside the chart
        show: !isMobile,
        text: chartData.title,
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'normal' }
      },
      tooltip: {
        trigger: 'axis',
        confine: true, // Keep tooltip within chart container - prevents cutoff
        // Position tooltip to ensure it's always visible on mobile
        position: isMobile ? (point: any, params: any, dom: any, rect: any, size: any) => {
          // Calculate position to keep tooltip visible
          const x = point[0];
          const y = point[1];
          const tooltipWidth = size.contentSize[0];
          const tooltipHeight = size.contentSize[1];
          const chartWidth = size.viewSize[0];
          
          // If tooltip would be cut off on the left, position it to the right
          if (x < tooltipWidth) {
            return [x + 20, y];
          }
          // If tooltip would be cut off on the right, position it to the left
          if (x + tooltipWidth > chartWidth) {
            return [x - tooltipWidth - 20, y];
          }
          // If tooltip would be cut off at the top, move it down
          if (y < tooltipHeight) {
            return [x, y + 20];
          }
          // Default: position to the right of the point
          return [x + 10, y];
        } : undefined,
        formatter: (params: any) => {
          let result = `${params[0].axisValue}<br/>`;
          params.forEach((param: any) => {
            // Extract clean label (remove tag ID and unit from name if present)
            const cleanName = param.seriesName.replace(/\s*\([^)]+\)\s*\([^)]+\)$/, '').replace(/\s*\([^)]+\)$/, '');
            // Get unit from series data (preferred) or extract from series name
            const series = chartData.series.find(s => s.name === param.seriesName);
            const unit = series?.unit || (param.seriesName.match(/\(([^)]+)\)\s*\(([^)]+)\)$/) ? 
              param.seriesName.match(/\(([^)]+)\)\s*\(([^)]+)\)$/)?.[2] : 
              param.seriesName.match(/\(([^)]+)\)$/)?.[1]) || '';
            result += `${cleanName}: ${param.value} ${unit}<br/>`;
          });
          return result;
        },
        // Add padding to tooltip for better mobile visibility
        padding: isMobile ? [8, 12] : [5, 10],
        // Ensure tooltip has proper background and border on mobile
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          fontSize: isMobile ? 12 : 12
        }
      },
      legend: {
        // Hide legend on mobile - we'll render it outside the chart
        show: !isMobile,
        // CRITICAL: Legend data must match series names exactly for ECharts selection to work
        // Use full series names so legend clicks properly toggle series
        data: chartData.series.map(s => s.name),
        formatter: (name: string) => {
          // Clean up legend labels for display - remove tag ID, keep label and unit
          // Format: "Label (TagId) (Unit)" -> "Label (Unit)"
          const match = name.match(/^(.*?)\s*\([^)]+\)\s*\(([^)]+)\)$/);
          if (match && match.length > 2) {
            return `${match[1]} (${match[2]})`; // "Label (Unit)"
          }
          // Fallback: "Label (TagId)" -> "Label"
          return name.replace(/\s*\([^)]+\)\s*\([^)]+\)$/, '').replace(/\s*\([^)]+\)$/, '');
        },
        top: 30
      },
      xAxis: {
        type: 'category',
        data: chartData.timeLabels,
        axisLabel: {
          // On mobile, use horizontal labels with fewer shown, or rotate less
          rotate: isMobile 
            ? (chartData.timeLabels.length > 12 ? 30 : 0)  // Less rotation on mobile
            : (chartData.timeLabels.length > 10 ? 45 : 0), // Desktop behavior
          fontSize: isMobile ? 11 : 10,
          // On mobile, show fewer labels
          interval: isMobile && chartData.timeLabels.length > 12 
            ? Math.ceil(chartData.timeLabels.length / 8) 
            : 0,
          // Format labels for mobile - show only time part
          formatter: isMobile && chartData.timeLabels.length > 8
            ? (value: string) => {
                const timeMatch = value.match(/(\d{2}:\d{2})/);
                return timeMatch ? timeMatch[1] : value.length > 8 ? value.substring(0, 8) : value;
              }
            : undefined
        }
      },
      yAxis: (() => {
        // If we have multiple distinct units (including percentages), create multiple Y-axes
        if (hasMultipleUnits) {
          const yAxes: any[] = [];
          
          // Always create left axis for percentages if present
          if (hasPercentages) {
            yAxes.push({
              type: 'value',
              name: '%',
              position: 'left',
              min: 0,
              max: 100,
              axisLabel: {
                formatter: '{value}%'
              },
              splitLine: {
                show: false // Don't show grid lines for percentage axis
              }
            });
          }
          
          // Create separate Y-axes for each distinct non-percentage unit
          // Position them on the right side, offset for multiple axes
          // CRITICAL: ECharts right-side offsets must be NEGATIVE to move axes left
          nonPercentUnits.forEach((unit, index) => {
            const position = hasPercentages ? 'right' : index === 0 ? 'left' : 'right';
            
            // Smart positioning: Right-side axes start closer to chart, increased offset for multiple axes
            // Negative offset moves left (toward chart), positive moves right (away from chart)
            // For right-side: use larger negative offset to position labels closer to chart
            let offset = 0;
            if (position === 'right') {
              // First right axis: position closer to chart (larger negative to move left)
              // Additional axes: increased positive offset to stack them with more spacing
              offset = index === 0 ? -25 : (-25 + (index * 80));
            }
            
            yAxes.push({
              type: 'value',
              name: unit,
              position: position,
              offset: offset,
              axisLabel: {
                formatter: (value: number) => `${value} ${unit}`,
                // Minimal margin - labels should start at chart edge
                margin: position === 'right' ? 8 : 8
              },
              nameLocation: 'end', // Position name at the end (top) of axis
              nameGap: position === 'right' ? 20 : 40, // Reduced gap for right-side to move label left (more visible)
              nameTextStyle: {
                align: position === 'right' ? 'right' : 'left' // Align name text appropriately
              },
              splitLine: {
                show: index === 0 // Only show grid for first non-percentage axis
              }
            });
          });
          
          return yAxes;
        }

        // Single unit - check if it's percentage
        const unit = distinctUnits[0] || '';
        const isPercentage = unit === '%';

        return {
          type: 'value',
          min: isPercentage ? 0 : undefined,
          max: isPercentage ? 100 : undefined,
          axisLabel: {
            formatter: (value: number) => `${value} ${unit}`
          }
        };
      })(),
      series: (() => {
        return chartData.series.map((series, index) => {
          // Get unit for this series
          const seriesUnit = getUnit(series);
          
          // Convert NaN to null for ECharts (ECharts uses null for missing data, not NaN)
          const dataWithNulls = series.data.map(v => (isNaN(v) ? null : v));
          
          const baseSeries: any = {
            name: series.name,
            data: dataWithNulls,
            type: chartData.chartType === 'bar' ? 'bar' : 'line',
            // CRITICAL: Disable smooth for sparse data - smooth interpolation can connect across nulls
            // Only enable smooth if we have dense data (more than 50% valid points)
            smooth: (chartData.chartType === 'line' || chartData.chartType === 'area') && 
                    (dataWithNulls.filter(v => v !== null && !isNaN(v)).length / dataWithNulls.length) > 0.5,
            lineStyle: { color: series.color },
            itemStyle: { color: series.color },
            // Configure for sparse data: show symbols for data points, don't connect across nulls
            connectNulls: false,
            showSymbol: true,
            symbolSize: 6,
            // Ensure null values are properly handled
            symbol: 'circle',
            symbolKeepAspect: true
          };

          // If multiple units, assign series to appropriate Y-axis
          // CRITICAL: Only set yAxisIndex if yAxis is actually an array (hasMultipleUnits = true)
          if (hasMultipleUnits) {
            if (seriesUnit === '%') {
              baseSeries.yAxisIndex = 0; // Left axis (percentage)
            } else {
              // Find the index of this unit in the non-percentage units array
              // Y-axis index = (hasPercentages ? 1 : 0) + unitIndex
              const unitIndex = nonPercentUnits.indexOf(seriesUnit);
              if (unitIndex >= 0) {
                baseSeries.yAxisIndex = (hasPercentages ? 1 : 0) + unitIndex;
              }
            }
          }

          if (chartData.chartType === 'area') {
            baseSeries.areaStyle = { color: series.color.replace(')', ', 0.2)').replace('rgb', 'rgba') };
          }

          return baseSeries;
        });
      })(),
      grid: {
        left: isMobile ? '8%' : '3%',  // More left space on mobile for Y-axis labels
        right: isMobile ? '8%' : '6%', // Reduced right space since axes are closer to chart
        bottom: isMobile ? '15%' : '10%', // More bottom space on mobile for X-axis labels
        top: isMobile ? '5%' : '10%',
        containLabel: true
      }
    };

    return option;
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div 
        className={`flex max-w-[95%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
        style={{ minWidth: 0 }} // CRITICAL: Allow flex container to shrink below content size
      >
        {/* Avatar */}
        <div className={`flex-shrink-0 ${isUser ? 'ml-3' : 'mr-3'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isUser 
              ? 'bg-blue-500 text-white' 
              : 'bg-soleco-500 text-white'
          }`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>

        {/* Message Content */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`} style={{ minWidth: 0, maxWidth: '100%' }}>
          <div className={`px-4 py-3 rounded-2xl ${
            isUser 
              ? 'bg-blue-500 text-white rounded-br-md' 
              : 'bg-gray-100 text-gray-800 rounded-bl-md'
          }`} style={{ minWidth: 0, maxWidth: '100%' }}>
            <div className="flex items-center gap-2 mb-2">
              {!isUser && message.data?.type && getDataIcon(message.data.type)}
              <span className="text-sm font-medium">
                {isUser ? t('chat.userLabel') : t('chat.assistantLabel')}
              </span>
            </div>
            
            {/* Render content as structured paragraphs */}
            <div className="text-sm leading-relaxed space-y-3">
              {message.content
                .split(/\n\n+/)
                .filter(para => para.trim().length > 0)
                .map((paragraph, index) => (
                  <p key={index} className="whitespace-pre-wrap">
                    {paragraph.trim()}
                  </p>
                ))}
            </div>
            
            {/* Chart visualization for bot messages */}
            {!isUser && message.chartData && (
              <div 
                ref={chartWrapperRef}
                data-chart-wrapper
                className="mt-3 pt-3 border-t border-gray-200" 
                style={{ 
                  width: '100%', 
                  // Use FULL width of chat box
                  maxWidth: '100%',
                  overflow: 'visible',
                  boxSizing: 'border-box',
                  // Prevent flex children from overflowing
                  minWidth: 0,
                  // Minimal padding to maximize chart width while allowing tooltip overflow
                  paddingRight: '0px',
                  paddingLeft: '0px'
                }}
              >
                {/* Title outside chart on mobile only */}
                {isMobile && (
                  <div className="mb-3 text-center">
                    <h3 className="text-sm font-medium text-gray-800">{message.chartData.title}</h3>
                  </div>
                )}
                
                {/* Conditionally render mobile or desktop chart */}
                {isMobile ? (
                  <ErrorBoundary fallback={
                    <ReactECharts
                      option={buildChartOption(message.chartData)}
                      style={{ height: '350px', width: '100%' }}
                      opts={{ renderer: 'svg' }}
                      onChartReady={(chart) => {
                        chartInstanceRef.current = chart;
                        chart.on('legendselectchanged', (params: any) => {
                          setHiddenSeries(prev => {
                            const newSet = new Set(prev);
                            if (params.selected[params.name]) {
                              newSet.delete(params.name);
                            } else {
                              newSet.add(params.name);
                            }
                            return newSet;
                          });
                        });
                      }}
                    />
                  }>
                    <MobileChart
                      chartData={message.chartData}
                      hiddenSeries={hiddenSeries}
                      onSeriesToggle={handleLegendClick}
                    />
                  </ErrorBoundary>
                ) : (
                <ReactECharts
                  option={buildChartOption(message.chartData)}
                  style={{ height: isMobile ? '350px' : '450px', width: '100%' }}
                  opts={{ renderer: 'svg' }}
                    onChartReady={(chart) => {
                      // Store chart instance for legend click handling
                      chartInstanceRef.current = chart;
                      
                      // Listen to legend select events to keep state in sync
                      chart.on('legendselectchanged', (params: any) => {
                        setHiddenSeries(prev => {
                          const newSet = new Set(prev);
                          if (params.selected[params.name]) {
                            newSet.delete(params.name);
                          } else {
                            newSet.add(params.name);
                          }
                          return newSet;
                        });
                      });
                    }}
                  />
                )}
                
                {/* Legend outside chart on mobile only - clickable */}
                {isMobile && (
                  <div className="mt-3 flex flex-wrap justify-center gap-4">
                    {message.chartData.series.map((series, index) => {
                      // Clean up legend labels for display - remove tag ID, keep label and unit
                      const match = series.name.match(/^(.*?)\s*\([^)]+\)\s*\(([^)]+)\)$/);
                      const displayName = match && match.length > 2
                        ? `${match[1]} (${match[2]})`
                        : series.name.replace(/\s*\([^)]+\)\s*\([^)]+\)$/, '').replace(/\s*\([^)]+\)$/, '');
                      
                      const isHidden = hiddenSeries.has(series.name);
                      
                      return (
                        <button
                          key={index}
                          onClick={() => handleLegendClick(series.name)}
                          className="flex items-center gap-2 touch-manipulation active:opacity-70 transition-opacity"
                          type="button"
                        >
                          <div 
                            className={`w-3 h-3 rounded-full transition-opacity ${isHidden ? 'opacity-30' : 'opacity-100'}`}
                            style={{ backgroundColor: series.color }}
                          />
                          <span className={`text-xs transition-opacity ${isHidden ? 'opacity-30 text-gray-400' : 'text-gray-700'}`}>
                            {displayName}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            {/* Data visualization for bot messages */}
            {!isUser && message.data && !message.chartData && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                {message.data.type === 'pv_production' && message.data.value !== undefined && (
                  <div className="flex items-center gap-2 text-sm">
                    <Sun className="w-4 h-4 text-yellow-500" />
                    <span className="font-medium">
                      {formatValue(message.data.value, message.data.unit || 'kWh')}
                    </span>
                  </div>
                )}
                
                {message.data.type === 'consumption' && message.data.value !== undefined && (
                  <div className="flex items-center gap-2 text-sm">
                    <Zap className="w-4 h-4 text-blue-500" />
                    <span className="font-medium">
                      {formatValue(message.data.value, message.data.unit || 'kWh')}
                    </span>
                  </div>
                )}
                
                {message.data.type === 'room_temperature' && message.data.temperatures && (
                  <div className="space-y-1">
                    {message.data.temperatures.map((temp: any, index: number) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <Thermometer className="w-4 h-4 text-red-500" />
                        <span className="font-medium">
                          {temp.room}: {temp.temperature}°C
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <span className="text-xs text-gray-500 mt-1">
            {format(message.timestamp, 'HH:mm', { locale: dateLocale })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
